import PageHeader from "../components/PageHeader";
import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowUpRight, Check } from "lucide-react";
import { COMPANY } from "../lib/content";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    subject: "", interest: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", email: "", company: "", phone: "", subject: "", interest: "", message: "" });
      toast.success("Message received. We'll reply shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Get in touch."
        subtitle="Reach a senior representative at BNM Group — by phone, email or the form below."
        image="https://static.prod-images.emergentagent.com/jobs/992269a5-240d-47a2-94bf-57af67715604/images/107173358a58b4fd951837fe4813b7bcfbd17e66dd1538255a609f5864d5d57d.png"
        testId="contact-page-header"
      />

      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-5 space-y-10">
            <div>
              <div className="text-eyebrow text-[#F37021] mb-3">Headquarters</div>
              <div className="flex gap-3 text-[#0A1F3F]">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0" />
                <p className="leading-relaxed">{COMPANY.address}</p>
              </div>
            </div>
            <div>
              <div className="text-eyebrow text-[#F37021] mb-3">Direct</div>
              <ul className="space-y-3 text-[#0A1F3F]">
                <li className="flex gap-3">
                  <Phone className="w-5 h-5 mt-0.5" />
                  <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-[#F37021]" data-testid="contact-phone">
                    {COMPANY.phone}
                  </a>
                </li>
                <li className="flex gap-3">
                  <Mail className="w-5 h-5 mt-0.5" />
                  <a href={`mailto:${COMPANY.email}`} className="hover:text-[#F37021]" data-testid="contact-email">
                    {COMPANY.email}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <div className="text-eyebrow text-[#F37021] mb-3">Offices</div>
              <ul className="text-sm text-[#4B5668] space-y-2">
                {COMPANY.offices.map((o) => (
                  <li key={o}>{o}</li>
                ))}
              </ul>
            </div>
            <div className="aspect-video border border-[#E3E6EC] overflow-hidden">
              <iframe
                title="BNM Group HQ"
                src="https://www.google.com/maps?q=Next+Level+Ankara+Kızılırmak+Dumlupınar&output=embed"
                loading="lazy"
                className="w-full h-full"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>

          <div className="lg:col-span-7">
            {success ? (
              <div
                className="bg-white border border-[#E3E6EC] p-12 text-center"
                data-testid="contact-success"
              >
                <div className="w-16 h-16 bg-[#F37021] text-white flex items-center justify-center mx-auto">
                  <Check className="w-7 h-7" />
                </div>
                <h3 className="mt-6 font-display font-black tracking-tighter text-3xl text-[#0A1F3F]">
                  Message received.
                </h3>
                <p className="mt-3 text-[#4B5668]">
                  A senior representative will be in touch shortly.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-8 inline-flex items-center gap-3 border border-[#0A1F3F] hover:bg-[#0A1F3F] hover:text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
                  data-testid="contact-new-message"
                >
                  Send another message
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-white border border-[#E3E6EC] p-8 sm:p-12 space-y-6"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Full name" name="name" value={form.name} onChange={onChange} required testId="contact-field-name" />
                  <Field label="Work email" type="email" name="email" value={form.email} onChange={onChange} required testId="contact-field-email" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Company" name="company" value={form.company} onChange={onChange} testId="contact-field-company" />
                  <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="contact-field-phone" />
                </div>
                <Field label="Subject" name="subject" value={form.subject} onChange={onChange} required testId="contact-field-subject" />
                <div>
                  <label className="text-eyebrow text-[#4B5668] mb-2 block">Message</label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-field-message"
                    className="w-full border border-[#E3E6EC] bg-[#F4F5F8] px-4 py-3 focus:outline-none focus:border-[#0A1F3F] resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-3 bg-[#F37021] hover:bg-[#D35F16] text-white px-10 py-5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required = false, testId }) {
  return (
    <div>
      <label className="text-eyebrow text-[#4B5668] mb-2 block">
        {label} {required && <span className="text-[#F37021]">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full border border-[#E3E6EC] bg-[#F4F5F8] px-4 py-3 focus:outline-none focus:border-[#0A1F3F]"
      />
    </div>
  );
}
